import React, { useState, useMemo, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Pagination, Typography,
    Box, styled, useTheme, IconButton, Tooltip, Checkbox, TableSortLabel, CircularProgress, Select,
    MenuItem, Popover, Button,
    Skeleton,
    Autocomplete,
    useMediaQuery,
    alpha
} from '@mui/material';
import {
    Search, Refresh, FilterList
} from '@mui/icons-material';
import FilterPopover from './FilterPopover';
import DataExporter from './DataExporter';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(0.8, 1.2),
    fontSize: '0.8rem',
    lineHeight: 1.3,
    transition: 'all 0.2s ease',

    '&.MuiTableCell-head': {
        backgroundColor: theme.palette.primary.main,
        fontWeight: 600,
        color: theme.palette.primary.contrastText,
        padding: theme.spacing(0.8, 1.4),
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },

    '&.MuiTableCell-body': {
        color: theme.palette.text.primary,
    },

    '&.MuiTableCell-root': {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },

    '&:first-of-type': {
        paddingLeft: theme.spacing(1.5),
    },
    '&:last-of-type': {
        paddingRight: theme.spacing(1.5),
    },

    // Zebra striping for even rows
    'tr:nth-of-type(even) &': {
        backgroundColor: theme.palette.grey[200], // Light gray for even rows
    },
}));

const DataTable = ({
    columns = [],
    data = [],
    rowsPerPageOptions = [10, 25, 50],
    defaultRowsPerPage = 10,
    selectable = false,
    onSelectionChange = () => { },
    extra = true,
    actions = null,
    download = { enabled: false, excludeColumns: [], filename: '' },
    footer = null,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
    const [selected, setSelected] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [filterConfig, setFilterConfig] = useState({});
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [loading, setLoading] = useState(false);

    const onRefresh = () => {
        setLoading(true);
        setTimeout(() => setLoading(false), 1000);
    };

    const uniqueValues = useMemo(() => {
        const map = {};
        columns.forEach(col => {
            if (col.filterType === 'select') {
                map[col.key] = [...new Set(data?.map(item => item[col.key]))].filter(Boolean);
            }
        });
        return map;
    }, [columns, data]);

    const processedData = useMemo(() => {
        let result = data !== null ? [...data] : [];

        if (searchTerm) {
            result = result.filter(row =>
                columns.some(col => {
                    const value = row[col.key];
                    return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
                })
            );
        }

        Object.entries(filterConfig).forEach(([key, filter]) => {
            if (filter?.value) {
                result = result.filter(row => {
                    const cellValue = row[key];
                    if (filter.type === 'text') {
                        return cellValue?.toString().toLowerCase().includes(filter.value.toLowerCase());
                    } else if (filter.type === 'boolean') {
                        return cellValue === filter.value;
                    } else if (filter.type === 'select') {
                        // Add check for empty array
                        if (Array.isArray(filter.value) && filter.value.length === 0) {
                            return true;
                        }
                        return filter.value.includes(cellValue);
                    } else if (filter.type === 'date') {
                        const rowDateObj = new Date(cellValue);
                        const filterDateObj = new Date(filter.value);

                        if (isNaN(rowDateObj) || isNaN(filterDateObj)) {
                            return false;
                        }

                        const rowDate = rowDateObj.toISOString().split('T')[0];
                        const filterDate = filterDateObj.toISOString().split('T')[0];
                        return rowDate === filterDate;
                    }
                    return true;
                });
            }
        });


        if (sortConfig.key) {
            result.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return result;
    }, [data, searchTerm, sortConfig, filterConfig, columns]);

    const paginatedData = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return processedData.slice(start, start + rowsPerPage);
    }, [processedData, page, rowsPerPage]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
        setPage(1);
    };

    const handleFilterChange = (key, value, type) => {
        setFilterConfig(prev => {
            // If the value is empty array or falsy, remove the filter
            if ((Array.isArray(value) && value.length === 0) || !value) {
                const newConfig = { ...prev };
                delete newConfig[key];
                return newConfig;
            }
            return {
                ...prev,
                [key]: { value, type }
            };
        });
        setPage(1);
    };
    const clearAllFilters = () => {
        setFilterConfig({});
        setPage(1);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const allRows = paginatedData;
            setSelected(allRows);
            onSelectionChange(allRows);
        } else {
            setSelected([]);
            onSelectionChange([]);
        }
    };

    const handleSelect = (event, row) => {
        const isAlreadySelected = selected.some(sel => sel === row);
        let newSelected;

        if (!isAlreadySelected) {
            newSelected = [...selected, row];
        } else {
            newSelected = selected.filter(sel => sel !== row);
        }

        setSelected(newSelected);
        onSelectionChange(newSelected);
    };

    useEffect(() => {
        if (selected.length > 0) {
            setSelected([]);
        }
    }, [data]);

    const handleFilterClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setFilterAnchorEl(null);
    };

    const filterOpen = Boolean(filterAnchorEl);

    return (
        <Box>
            {extra && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: 1.5,
                        mb: 1,
                        width: '100%',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {selected.length > 0 && selectable && (
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                {selected.length} {selected.length === 1 ? 'row' : 'rows'} selected
                            </Typography>
                        )}

                        {actions && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {actions}
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder={`Search ${processedData.length} records...`}
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setPage(1);
                            }}
                            InputProps={{
                                startAdornment: <Search fontSize="small" sx={{ mr: 0.5 }} />,
                                sx: {
                                    borderRadius: 1,
                                    width: { xs: '100%', sm: 180, md: 260 },
                                    height: 32,
                                    '& input': { py: 0.70, fontSize: '0.75rem' },
                                },
                            }}
                        />

                        {/* <Tooltip title="Refresh">
                            <IconButton size="small" onClick={onRefresh} sx={{ p: 0.75 }}>
                                <Refresh fontSize="small" />
                            </IconButton>
                        </Tooltip> */}

                        <Tooltip title="Filter">
                            <IconButton size="small" onClick={handleFilterClick} sx={{ p: 0.75 }}>
                                <FilterList fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        {download.enabled && (
                            <DataExporter
                                columns={columns}
                                data={data}
                                fileName={download?.filename}
                                excludeColumns={download.excludeColumns}
                            />
                        )}
                    </Box>
                </Box>
            )}


            <Popover
                open={filterOpen}
                anchorEl={filterAnchorEl}
                onClose={handleFilterClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <FilterPopover
                    columns={columns}
                    handleFilterChange={handleFilterChange}
                    clearAllFilters={clearAllFilters}
                    filterConfig={filterConfig}
                    uniqueValues={uniqueValues}
                />
            </Popover>

            <TableContainer sx={{ maxHeight: 390, position: 'relative' }}>
                <Table stickyHeader size="small">
                    <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2, whiteSpace: 'nowrap' }}>
                        <TableRow>
                            {selectable && (
                                <StyledTableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selected.length > 0 && selected.length < processedData.length}
                                        checked={processedData.length > 0 && selected.length === processedData.length}
                                        onChange={handleSelectAll}
                                    />
                                </StyledTableCell>
                            )}
                            {columns.map(col => (
                                <StyledTableCell key={col.key}>
                                    <TableSortLabel
                                        active={sortConfig.key === col.key}
                                        direction={sortConfig.direction}
                                        onClick={() => handleSort(col.key)}
                                    >
                                        {col.label}
                                    </TableSortLabel>
                                </StyledTableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading && (
                            Array.from({ length: paginatedData }).map((_, index) => (
                                <TableRow key={index}>
                                    {columns.map((_, i) => (
                                        <StyledTableCell key={i}>
                                            <Skeleton variant="text" />
                                        </StyledTableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                        {!loading && paginatedData.length === 0 ? (
                            <TableRow>
                                <StyledTableCell colSpan={columns.length + (selectable ? 1 : 0)} align="center">
                                    <Typography variant="body2" color="text.primary">No records found</Typography>
                                </StyledTableCell>
                            </TableRow>
                        ) : (
                            paginatedData.map((row, index) => {
                                const serialNumber = (page - 1) * rowsPerPage + index + 1;
                                // const isItemSelected = isSelected(serialNumber);
                                const isItemSelected = selected.includes(row);
                                return (
                                    <TableRow key={serialNumber} hover>
                                        {selectable && (
                                            <StyledTableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isItemSelected}
                                                    onChange={(event) => handleSelect(event, row)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </StyledTableCell>
                                        )}
                                        {columns.map(col => (
                                            <StyledTableCell key={col.key}>
                                                {col.render ? col.render(row, serialNumber) : (row[col.key] ?? '—')}
                                            </StyledTableCell>
                                        ))}
                                    </TableRow>
                                );
                            })
                        )}
                        {footer}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Rows per page:</Typography>
                    <Autocomplete
                        size="small"
                        options={rowsPerPageOptions}
                        getOptionLabel={(option) => option.toString()}
                        value={rowsPerPage}
                        onChange={(event, newValue) => {
                            const numValue = parseInt(newValue || 0, 10); // Default to 10 if empty
                            if (!isNaN(numValue)) {
                                setRowsPerPage(Math.max(1, numValue)); // Ensure minimum 1 row
                                setPage(1);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                sx={{ width: 80 }}
                                inputProps={{
                                    ...params.inputProps,
                                    type: 'number',
                                    min: 1,
                                    onKeyDown: (e) => {
                                        // Prevent negative numbers and decimal points
                                        if (e.key === '-' || e.key === '.' || e.key === 'e') {
                                            e.preventDefault();
                                        }
                                    },
                                }}
                            />
                        )}
                        disableClearable
                        freeSolo
                        inputValue={rowsPerPage.toString()}
                        onInputChange={(event, newInputValue) => {
                            if (newInputValue === '') {
                                // When cleared, set to default (10)
                                setRowsPerPage(0);
                                setPage(1);
                                return;
                            }

                            const numValue = parseInt(newInputValue, 10);
                            if (!isNaN(numValue)) {
                                setRowsPerPage(Math.max(1, numValue)); // Ensure minimum 1 row
                                setPage(1);
                            }
                        }}
                    />
                </Box>
                {!isMobile && (
                    <Typography variant="body2">
                        Showing {paginatedData.length} of {processedData.length} records
                    </Typography>
                )}
                <Pagination
                    count={Math.ceil(processedData.length / rowsPerPage)}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    shape="rounded"
                    size="small"
                />
            </Box>
        </Box>
    );
};

export default React.memo(DataTable);
